import { useCallback, useEffect, useState } from 'react';
import { api, type Organization } from '../lib/api';
import { authApi, type AdminUser } from '../lib/authApi';
import { Icon } from '../ui/icons';

/**
 * Every organisation on the server, membership filter lifted — plus the one
 * place organisations are created.
 *
 * Creation lives here and nowhere else because ownership is the super
 * admin's call: an organisation is handed to a named owner rather than
 * claimed by whoever clicked the button.
 */
export function OrgsTab({ onOpenOrg }: { onOpenOrg: (orgId: string) => void }) {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [orgList, userList] = await Promise.all([
        authApi.adminListOrgs(),
        authApi.adminListUsers(),
      ]);
      setOrgs(orgList);
      setUsers(userList);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить организации');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await api.createOrg(trimmed, ownerId || undefined);
      setName('');
      setOwnerId('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось создать организацию');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="dash-loading">Загрузка…</div>;

  return (
    <div className="admin-tab">
      {error && <div className="dash-error">{error}</div>}

      <div className="admin-create-org">
        <input
          className="dash-inline-input"
          placeholder="Название организации"
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void create();
          }}
        />
        <select
          className="org-member-role"
          value={ownerId}
          onChange={(e) => setOwnerId(e.target.value)}
        >
          <option value="">Владелец — я</option>
          {users
            .filter((u) => !u.disabled)
            .map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
        </select>
        <button type="button" className="primary-btn" disabled={busy} onClick={() => void create()}>
          <Icon name="plus" size={15} /> Создать
        </button>
      </div>

      <div className="admin-table">
        {orgs.map((org) => (
          <button
            key={org.id}
            type="button"
            className="admin-row admin-row-org"
            onClick={() => onOpenOrg(org.id)}
          >
            <span className="admin-user-avatar" style={{ background: org.color }}>
              {org.name.slice(0, 1).toUpperCase()}
            </span>
            <span className="admin-user-text">
              <span className="admin-user-name">{org.name}</span>
              <span className="admin-user-meta">
                {org.boardCount ?? 0} досок · {org.eventCount ?? 0} событий
                {(org.onlineUsers ?? 0) > 0 && ` · ${org.onlineUsers} онлайн`}
              </span>
            </span>
            <Icon name="arrowLeft" size={14} className="admin-row-go" />
          </button>
        ))}
      </div>
    </div>
  );
}
